import React, { useState, useEffect, useCallback } from 'react';
import logger from '../../../utils/logger';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Tr,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Table,
  useColorModeValue
} from '@chakra-ui/react';
import CustomInput from '../../../components/Input';
import { BANDWIDTH, ENERGY } from '../../../common/utils/constants';
import { toTron } from '../../../common/utils';
import PlusMinusInput from '../../../components/PlusMinusInput';
import ModalComponent from '../../../components/Modal';
import isEmpty from 'is-empty';
import {
  getAccountV2,
  getTransactionResource,
  transferHandler,
} from '../../../common/api';
import { useSelector } from 'react-redux';

export default function Order(props) {
  const { onClose, isOpen, isMobile } = props;

  const { accountAddress, accountBalance, tronResources } = useSelector(
    (state) => state.account
  );
  const [isWalletBalanceSufficient, setIsWalletBalanceSufficient] =
    useState(false);
  const [accountActive, setAccountActive] = useState(true);
  const [transactionResource, setTransactionResource] = useState({});
  const [resourceType, setResourceType] = useState(ENERGY);
  const toast = useToast();
  const [orderLoader, setOrderLoader] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    address: accountAddress,
    amount: 1000000,
    pricePerEnergyDay: 50,
    pricePerBandwidthDay: 560,
    orderAmount: 0,
  });
  const [energyPrices, setEnergyPrices] = useState({
    minEnergyPrice: 25,
    maxEnergyPrice: 280,
  });
  const [bandwidthPrices, setBandwidthPrices] = useState({
    minBandwidthPrice: 500,
    maxBandwidthPrice: 1000,
  });

  const calculateOrderAmount = (amount, freezeDuration, value) => {
    if (!amount || !freezeDuration || !value || isNaN(amount) || isNaN(freezeDuration) || isNaN(value)) {
      return 0;
    }
    return amount * freezeDuration * value;
  };

  useEffect(() => {
    getTransactionResource()
      .then((value) => {
        setTransactionResource(value);
        setOrderDetails((prev) => ({
          ...prev,
          pricePerEnergyDay: value?.Min_Energy_Price_Per_Day,
          pricePerBandwidthDay: value?.Min_Bandwidth_Price_Per_Day,
        }));
        setEnergyPrices((prev) => ({
          ...prev,
          minEnergyPrice: value?.Min_Energy_Price_Per_Day,
        }));
        setBandwidthPrices((prev) => ({
          ...prev,
          minBandwidthPrice: value?.Min_Bandwidth_Price_Per_Day,
        }));
      })
      .catch((err) => {
        logger.error('Error fetching bandwidth prices:', err);
      });
  }, [resourceType]);

  const updateOrderAmount = useCallback(
    (amount, freezeDuration, pricePerDay) => {
      const orderAmount = calculateOrderAmount(
        amount,
        freezeDuration,
        pricePerDay
      );
      setOrderDetails((prev) => ({ ...prev, orderAmount: orderAmount || 0 }));
    },
    []
  );

  useEffect(() => {
    if (resourceType === ENERGY) {
      updateOrderAmount(
        orderDetails.amount,
        transactionResource.Freeze_Duration,
        orderDetails.pricePerEnergyDay
      );
    } else {
      updateOrderAmount(
        orderDetails.amount,
        transactionResource.Freeze_Duration,
        orderDetails.pricePerBandwidthDay
      );
    }
  }, [
    orderDetails.amount,
    orderDetails.pricePerBandwidthDay,
    orderDetails.pricePerEnergyDay,
    resourceType,
    transactionResource,
    updateOrderAmount,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrderDetails((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (accountAddress)
      setOrderDetails({ ...orderDetails, address: accountAddress });
  }, [accountAddress]);

  useEffect(() => {
    accountBalance <= toTron(orderDetails.orderAmount)
      ? setIsWalletBalanceSufficient(true)
      : setIsWalletBalanceSufficient(false);
  }, [orderDetails]);

  const updatePricePerDay = (value, name) => {
    const orderAmount = calculateOrderAmount(orderDetails.amount, 3, value);
    setOrderDetails((prev) => ({
      ...prev,
      [name]: value,
      orderAmount: orderAmount,
    }));
  };
  const handleBandwidthChange = (e) => {
    if (isEmpty(accountAddress)) return;
    const { name, value } = e.target;
    setOrderDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleEnergyAmountChange = (e) => {
    const { value } = e.target;
    updateOrderAmount(
      value,
      transactionResource.Freeze_Duration,
      orderDetails.pricePerEnergyDay
    );
    setOrderDetails((prev) => ({
      ...prev,
      amount: value,
    }));
  };

  const handleBandwidthAmountChange = (e) => {
    const { value } = e.target;
    updateOrderAmount(
      value,
      transactionResource.Freeze_Duration,
      orderDetails.pricePerBandwidthDay
    );
    setOrderDetails((prev) => ({
      ...prev,
      amount: value,
    }));
  };

  const orderHandler = async (order, resourceType) => {
    setOrderLoader(true);
    try {
      const res = await transferHandler(order, resourceType, accountAddress);
      toast({
        title: `Transaction completed successfully`,
        position: 'top-right',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
      setResourceType(ENERGY);
    } catch (error) {
      toast({
        title: error || `Something went wrong`,
        position: 'top-right',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setOrderLoader(false);
    }
  };

  const getSavingsAmount = () => {
    let marketPricePerDay =
      resourceType === ENERGY
        ? energyPrices.maxEnergyPrice
        : bandwidthPrices.maxBandwidthPrice;
    
    if (!marketPricePerDay || !orderDetails.amount || !orderDetails.orderAmount) {
      return 0;
    }
    
    let marketTotal = orderDetails.amount * 3 * marketPricePerDay;
    let savings = marketTotal - orderDetails.orderAmount;
    return isNaN(savings) ? 0 : savings;
  };

  const getDiscountPercentage = () => {
    let ourPricePerDay =
      resourceType === ENERGY
        ? orderDetails.pricePerEnergyDay
        : orderDetails.pricePerBandwidthDay;
    let marketPricePerDay =
      resourceType === ENERGY
        ? energyPrices.maxEnergyPrice
        : bandwidthPrices.maxBandwidthPrice;
    
    if (!ourPricePerDay || !marketPricePerDay || !orderDetails.amount) {
      return 0;
    }
    
    let actualPrice = marketPricePerDay * 3 * orderDetails.amount;
    let ourPrice = ourPricePerDay * 3 * orderDetails.amount;
    
    if (actualPrice === 0) return 0;
    
    let discountPercentage = ((actualPrice - ourPrice) / actualPrice) * 100;
    return isNaN(discountPercentage) ? 0 : discountPercentage.toFixed();
  };

  const validateAccount = async (address) => {
    const data = getAccountV2(address)
      .then((res) => {
        setAccountActive(res.data.activated);
      })
      .catch((err) => {
        logger.error('Error validating account:', err);
      });
  };

  return (
    <Box className='form_body energy-bandwidth'>
      <Tabs 
        variant='soft-rounded' 
        size={isMobile ? 'sm' : 'md'}
        index={resourceType === ENERGY ? 0 : 1}
      >
        <TabList 
          justifyContent='center' 
          mb={isMobile ? '12px' : '10px'} 
          backgroundColor={useColorModeValue('#f2f2f3', '#3e3d3c')}
          p={isMobile ? 1 : 2}
        >
          <Tab
            className=' '
            onClick={() => {
              setResourceType(ENERGY);
              setOrderDetails((prev) => ({
                ...prev,
                pricePerEnergyDay: 50,
                amount: 1000000,
              }));
            }}
          >
            Energy
          </Tab>

          <Tab
            className=' '
            ml='3'
            onClick={() => {
              setResourceType(BANDWIDTH);
              setOrderDetails((prev) => ({
                ...prev,
                pricePerEnergyDay: 560,
                amount: 10000,
              }));
            }}
          >
            Bandwidth
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel padding={'0'}>
            <Box mb={isMobile ? '12px' : '10px'}>
              <CustomInput
                label={isMobile ? 'Address' : 'Wallet address'}
                placeholder='Wallet address'
                value={orderDetails.address}
                name='address'
                type='text'
                onChange={handleChange}
                onBlur={() => validateAccount(orderDetails.address)}
                isInvalid={!accountActive}
                size={isMobile ? 'sm' : 'md'}
              />
              {!accountActive && (
                <Text fontSize='xs' color='crimson' mt='1'>
                  Address is not activated.{' '}
                </Text>
              )}
            </Box>
            <Box mb='10px'>
              <CustomInput
                label='Energy amount'
                placeholder='Energy amount'
                isInvalid={isWalletBalanceSufficient}
                errorBorderColor='crimson'
                value={orderDetails.amount}
                name='amount'
                type='number'
                onChange={handleEnergyAmountChange}
              />
              {isWalletBalanceSufficient && (
                <Text fontSize='xs' color='crimson' mt='1'>
                  Insufficient Wallet Balance
                </Text>
              )}
              {orderDetails.amount <
                transactionResource?.Minimum_Energy_order && (
                <Text fontSize='xs' color='crimson' mt='1'>
                  Min Amount : {transactionResource?.Minimum_Energy_order}
                </Text>
              )}
            </Box>
            <FormControl mb='3'>
              <FormLabel htmlFor='amount'>Price/day</FormLabel>
              <PlusMinusInput
                updateState={(value, name) => updatePricePerDay(value, name)}
                maxValue={energyPrices.maxEnergyPrice}
                minValue={energyPrices.minEnergyPrice}
                name='pricePerEnergyDay'
                value={orderDetails.pricePerEnergyDay}
              />
            </FormControl>
            <p>Delegation duration: 3 Days</p>
            <Box p='1' mt='3' bg={useColorModeValue('#F2F2F3', '#3e3d3c')} borderRadius={'12px'}>
              <Table variant='unstyled' size='sm'>
                <Tbody>
                  <Tr>
                    <Td padding={'2px 5px'} width={'100px'}>
                      Order amount
                    </Td>
                    <Td padding={'2px 5px'}>
                      : {toTron(orderDetails.orderAmount)} TRX
                    </Td>
                  </Tr>
                  <Tr>
                    <Td padding={'2px 5px'} width={'100px'}>
                      Savings
                    </Td>
                    <Td padding={'2px 5px'}>
                      :{' '}
                      <span className='green'>
                        {toTron(getSavingsAmount())}{' '}
                        TRX ({getDiscountPercentage()}%)
                      </span>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td padding={'2px 5px'} width={'100px'}>
                      TXN Fee
                    </Td>
                    <Td padding={'2px 5px'}>
                      : 0 TRX{' '}
                      <Text fontSize='xs' as='i' color={'orange'}>
                        Free Forever
                      </Text>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            <Stack direction='row' spacing={4} mt={isMobile ? 4 : '5'}>
              <Button
                width='100%'
                size={isMobile ? 'lg' : 'md'}
                backgroundColor='#222C34'
                _hover={{ backgroundColor: '#373E47' }}
                color='#fff'
                isDisabled={
                  orderDetails.amount >=
                    transactionResource?.Minimum_Energy_order &&
                  !isWalletBalanceSufficient &&
                  !orderLoader &&
                  accountActive
                    ? false
                    : true
                }
                onClick={() => {
                  orderHandler(orderDetails, ENERGY);
                }}
                isLoading={orderLoader}
              >
                Buy
              </Button>
            </Stack>
          </TabPanel>
          <TabPanel padding={'0'}>
            <Box mb={isMobile ? '12px' : '5'}>
              <CustomInput
                label={isMobile ? 'Address' : 'Wallet address'}
                placeholder='Wallet address'
                size={isMobile ? 'sm' : 'md'}
                value={orderDetails.address}
                name='address'
                onChange={handleBandwidthChange}
                type='text'
              />
            </Box>
            <Box mb='5'>
              <CustomInput
                label='Bandwidth amount'
                placeholder='Bandwidth amount'
                isInvalid={isWalletBalanceSufficient}
                errorBorderColor='crimson'
                value={orderDetails.amount}
                name='amount'
                onChange={handleBandwidthAmountChange}
                type='number'
              />
              {isWalletBalanceSufficient && (
                <Text fontSize='xs' color='crimson' mt='1'>
                  Insufficient Wallet Balance
                </Text>
              )}
              {orderDetails.amount <
                transactionResource?.Minimum_Bandwidth_order && (
                <Text fontSize='xs' color='crimson' mt='1'>
                  Min Amount : {transactionResource?.Minimum_Bandwidth_order}{' '}
                </Text>
              )}
            </Box>
            <FormControl mb='3'>
              <FormLabel htmlFor='amount'>Price/day</FormLabel>
              <PlusMinusInput
                updateState={(value, name) => updatePricePerDay(value, name)}
                maxValue={bandwidthPrices.maxBandwidthPrice}
                minValue={bandwidthPrices.minBandwidthPrice}
                name='pricePerBandwidthDay'
                value={orderDetails.pricePerBandwidthDay}
              />
            </FormControl>
            <p>Delegation duration: 3 Days</p>
            <Box p='1' mt='3' bg={useColorModeValue('#F2F2F3', '#3e3d3c')}  borderRadius={'12px'}>
              <Table variant='unstyled' size='sm'>
                <Tbody>
                  <Tr>
                    <Td padding={'2px 5px'} width={'100px'}>
                      Order amount
                    </Td>
                    <Td padding={'2px 5px'}>
                      : {toTron(orderDetails.orderAmount)} TRX
                    </Td>
                  </Tr>
                  <Tr>
                    <Td padding={'2px 5px'} width={'100px'}>
                      Savings
                    </Td>
                    <Td padding={'2px 5px'}>
                      :{' '}
                      <span className='green'>
                        {toTron(getSavingsAmount())}{' '}
                        TRX ({getDiscountPercentage()}%)
                      </span>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td padding={'2px 5px'} width={'100px'}>
                      TXN Fee
                    </Td>
                    <Td padding={'2px 5px'}>
                      : 0 TRX{' '}
                      <Text fontSize='xs' as='i' color={'orange'}>
                        Free Forever
                      </Text>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            <Stack direction='row' spacing={4} mt='5'>
              <Button
                color={useColorModeValue('#F1F1F1', '#3e3d3c')}
                _hover={{ backgroundColor: '#373E47' }}
                width='100%'
                isDisabled={
                  orderDetails.amount >=
                    transactionResource?.Minimum_Bandwidth_order &&
                  !isWalletBalanceSufficient &&
                  !orderLoader
                    ? false
                    : true
                }
                onClick={() => orderHandler(orderDetails, BANDWIDTH)}
                isLoading={orderLoader}
              >
                Buy
              </Button>
            </Stack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
